import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule, MatPrefix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SkillCategoryFormDialogComponent } from 'src/app/components/main/employees/rrhh/skill-category-form-dialog/skill-category-form-dialog.component';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { FormDialogModeEnum } from 'src/app/enums/form-dialog-mode.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { SkillCategory } from 'src/app/models/skill_category.model';
import { ApiService, UtilsService } from 'src/app/services';

@Component({
  selector: 'app-skill-categories-list',
  templateUrl: './skill-categories-list.component.html',
  styleUrl: './skill-categories-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatPrefix,
    MatTableModule,
    TypeSafeMatCellDeferred,
  ],
})
export class SkillCategoriesListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);

  dataSource = new MatTableDataSource<SkillCategory>([]);

  displayedColumns: string[] = ['name', 'description', 'slug', 'actions'];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
    parent_skill_category_id: [null as number | null],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.listenSearchFormControl();
  }

  openCreateDialog(): void {
    const dialog_ref = this.mat_dialog.open(SkillCategoryFormDialogComponent, {
      width: '480px',
      data: {
        mode: FormDialogModeEnum.Create,
        parent_skill_category_id: this.getParentSkillCategoryId(),
      },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
          this.fetchData();
        }
      });
  }

  openEditDialog(skill_category: SkillCategory): void {
    const dialog_ref = this.mat_dialog.open(SkillCategoryFormDialogComponent, {
      width: '480px',
      data: { mode: FormDialogModeEnum.Edit, skill_category },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.fetchData();
        }
      });
  }

  manageChildren(skill_category: SkillCategory): void {
    this.filterForm.patchValue({
      parent_skill_category_id: skill_category.id,
      page: 1,
    });
  }

  deleteSkillCategory(skill_category: SkillCategory): void {
    if (!confirm(`¿Eliminar la categoría "${skill_category.name}"?`)) {
      return;
    }

    this.api_service.deleteSkillCategory(skill_category.id).subscribe({
      next: (): void => {
        const current_page = Number(this.filterForm.get('page')?.value ?? 1);
        if (this.dataSource.data.length === 1 && current_page > 1) {
          this.filterForm.patchValue({ page: current_page - 1 }, { emitEvent: false });
        }
        this.fetchData();
        this.snack_bar.open('Categoría eliminada', undefined, { duration: 3000 });
      },
    });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';
    const parent_skill_category_id = this.getParentSkillCategoryId();

    const params: Record<string, unknown> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'name:asc',
    };

    if (search_term) {
      params.search = search_term;
    }

    if (parent_skill_category_id !== null) {
      params.parent_skill_category_id = parent_skill_category_id;
    }

    this.api_service.getSkillCategories(params).subscribe({
      next: (response: ArrayResponse<SkillCategory>): void => {
        this.dataSource.data = response.data;
        this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
        this.change_detector_ref.markForCheck();
      },
    });
  }

  private getParentSkillCategoryId(): number | null {
    const value = this.queryParameters()['parent_skill_category_id'];
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return Number(value);
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }
}
